import { Field, ErrorMessage, useFormikContext } from "formik";
import { Label, Input, FormFeedback } from "reactstrap";

import CreatorVisibleIndicator from "./components/CreatorVisibleIndicator";
import Uploader from "./components/Uploader";
import { InlineUploader } from "./components/Uploader/InlineUploader";

/**
 * @typedef {Object} CustomFieldProps
 * @property {string} fieldKey - The field key
 * @property {string} [type] - The field type
 * @property {string} [customType] - The custom field type
 * @property {string} [label] - The field label
 * @property {Function} [hideCondition] - Function to determine if field should be hidden
 * @property {Function} [disableCondition] - Function to determine if field should be disabled
 * @property {Object} [disableConditionCompareValues] - Values to compare for disable condition
 * @property {Object} [hideConditionCompareValues] - Values to compare for hide condition
 * @property {boolean} [creatorVisible] - Whether field is visible to creators
 * @property {Object} [fieldInputProps] - Props for field input
 */

/**
 * CustomField component that renders different field types based on the type prop
 * @param {CustomFieldProps} props
 */
const CustomField = ({
  fieldKey,
  type = "",
  customType = "text",
  label = undefined,
  hideCondition = undefined,
  disableCondition = undefined,
  // TODO: refactor below, this is messy
  disableConditionCompareValues = {},
  hideConditionCompareValues = {},
  creatorVisible = undefined,
  fieldInputProps = {},
  ...props
}) => {
  const { values } = useFormikContext();
  let disabled = false;

  if (hideCondition && hideCondition(values, hideConditionCompareValues)) {
    return null;
  }
  if (
    disableCondition
    && disableCondition(values, disableConditionCompareValues)
  ) {
    disabled = true;
  }

  return (
    <>
      {label && (
        <Label className="text-muted" htmlFor={fieldKey}>
          {/* // TODO: This is used by milestone forms and any form where label is above field */}
          <CreatorVisibleIndicator
            isVisible={!!creatorVisible}
            showIcon={true}
            iconPosition="static"
          >
            {label}
          </CreatorVisibleIndicator>
        </Label>
      )}
      <Field name={fieldKey}>
        {({ field, form, meta }) => {
          const isInvalid = !!(meta.touched && meta.error);
          switch (type) {
            case "switch":
              return (
                <Input
                  type="switch"
                  id={fieldKey}
                  name={fieldKey}
                  checked={field?.value}
                  onChange={() => {
                    form.setFieldValue(field?.name, !field?.value);
                  }}
                  invalid={isInvalid}
                  disabled={disabled}
                />
                // TODO: currency input
              );
            case "uploader":
              return (
                <Uploader
                  name={fieldKey}
                  accept={fieldInputProps?.accept}
                  multiple={fieldInputProps?.multiple}
                  disabled={disabled}
                  invalid={isInvalid}
                  {...fieldInputProps}
                />
              );
            case "inline-uploader":
              return (
                <InlineUploader
                  name={fieldKey}
                  accept={fieldInputProps?.accept}
                  disabled={disabled}
                  invalid={isInvalid}
                  {...fieldInputProps}
                />
              );
            case "custom":
              return (
                // @ts-expect-error - fieldInputProps is not typed
                <props.CustomRenderer
                  type={customType}
                  name={fieldKey}
                  id={fieldKey}
                  fieldKey={fieldKey}
                  value={field?.value}
                  disabled={disabled}
                  invalid={isInvalid}
                  {...props}
                  {...field}
                />
              );
            case "percentage":
              return (
                <Input
                  type="number"
                  id={fieldKey}
                  name={fieldKey}
                  invalid={isInvalid}
                  disabled={disabled}
                  min={0}
                  max={100}
                  step={0.01}
                  {...field}
                />
              );
            default:
              return (
                <Input
                  type={type}
                  id={fieldKey}
                  name={fieldKey}
                  invalid={isInvalid}
                  disabled={disabled}
                  {...field}
                />
              );
          }
        }}
      </Field>
      <ErrorMessage
        name={fieldKey}
        render={(msg) => {
          let message = "";
          if (msg && typeof msg === "string") {
            message = msg;
          }
          return <FormFeedback invalid={"true"}>{message}</FormFeedback>;
        }}
      ></ErrorMessage>
    </>
  );
};

export default CustomField;
